/*
 * Landing Page Tracker
 * ------------------------------------------------------------
 * When a customer visits a tracked landing page (matched by template
 * suffix), this stamps the cart with note attributes (landing page,
 * URL, referrer, UTM params, timestamp). Those attributes ride along
 * into checkout and show up on the order under "Additional details"
 * in the Shopify admin.
 *
 * If the page URL path contains "-aff", an "Affiliate" attribute is
 * also set to "true" (Shopify note attributes are always strings).
 *
 * Shopify Flow reads the "Landing Page" / "Affiliate" attributes on
 * order_created and tags the order accordingly — it doesn't need its
 * own copy of this list, so adding or removing a template prefix here
 * is the only change needed to track (or stop tracking) a landing family.
 *
 * Last-touch model: visiting a tracked landing page overwrites any
 * previous attribution stored from an earlier visit in this browser.
 * ------------------------------------------------------------
 */
(function () {
  var TRACKED_TEMPLATE_PREFIXES = [
    'nektar-magna',
    'planta-magna',
    'os-creatine',
    'kinetic-starter-kit',
    'kinetic-sk',
    'planta-solein-sk'
  ];

  var STORAGE_KEY = 'landing_attribution';
  var SESSION_SYNCED_KEY = 'landing_synced';

  function getUtmParams() {
    var params = new URLSearchParams(window.location.search);
    var utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(function (key) {
      var val = params.get(key);
      if (val) utm[key] = val;
    });
    return utm;
  }

  function currentTemplateSuffix() {
    var body = document.body;
    return body ? body.getAttribute('data-template-suffix') || '' : '';
  }

  function isAffiliateUrl() {
    return window.location.pathname.indexOf('-aff') !== -1;
  }

  function isTrackedTemplate(suffix) {
    if (!suffix) return false;
    return TRACKED_TEMPLATE_PREFIXES.some(function (prefix) {
      return suffix === prefix || suffix.indexOf(prefix) === 0;
    });
  }

  function resolveLandingPageId() {
    var suffix = currentTemplateSuffix();
    return isTrackedTemplate(suffix) ? suffix : '';
  }

  function buildAttributionRecord(landingPageId) {
    return {
      handle: landingPageId,
      url: window.location.href,
      referrer: document.referrer || '',
      utm: getUtmParams(),
      timestamp: new Date().toISOString(),
      affiliate: isAffiliateUrl()
    };
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStored(record) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {}
  }

  function syncCartAttributes(record) {
    var attributes = {
      'Landing Page': record.handle,
      'Landing URL': record.url,
      'Landing Referrer': record.referrer,
      'Landing Visited At': record.timestamp,
      // Empty string clears a prior affiliate stamp on non-aff last-touch visits.
      'Affiliate': record.affiliate ? 'true' : ''
    };
    Object.keys(record.utm || {}).forEach(function (key) {
      attributes['Landing ' + key] = record.utm[key];
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function () {
      try { window.sessionStorage.setItem(SESSION_SYNCED_KEY, '1'); } catch (e) {}
    }).catch(function () {});
  }

  function init() {
    var landingPageId = resolveLandingPageId();

    if (landingPageId) {
      var record = buildAttributionRecord(landingPageId);
      writeStored(record);
      syncCartAttributes(record);
      return;
    }

    // Affiliate-only pages (URL has "-aff" but template isn't in the
    // tracked landing list) still stamp Affiliate without overwriting
    // an existing Landing Page attribution from an earlier visit.
    if (isAffiliateUrl()) {
      var existing = readStored() || {};
      existing.affiliate = true;
      existing.url = existing.url || window.location.href;
      writeStored(existing);
      syncCartAttributes({
        handle: existing.handle || '',
        url: existing.url,
        referrer: existing.referrer || document.referrer || '',
        utm: existing.utm || getUtmParams(),
        timestamp: existing.timestamp || new Date().toISOString(),
        affiliate: true
      });
      return;
    }

    // Not a tracked landing page this time — make sure any attribution
    // captured earlier in this browser still rides along at least once
    // per session, in case the cart was recreated since that visit.
    var alreadySynced = false;
    try { alreadySynced = window.sessionStorage.getItem(SESSION_SYNCED_KEY) === '1'; } catch (e) {}
    if (alreadySynced) return;

    var stored = readStored();
    if (stored) syncCartAttributes(stored);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
